import React, { useCallback, useMemo, useState } from "react";
import { TaleBorderFul } from "@components/TaleBorderFul";
import Select, { components } from "react-select";

export const TaleSelect = ({
                             value,
                             onChange,
                             width = "100%",
                             height = 51,
                             role = "normal",
                             label = "",
                             labelClassName = "",
                             className = "",
                             children,
                             selectClassName = "",
                             multiple = false,
                             filterKey,  // new optional prop: the key to filter options by (e.g. "type")
                             filterValues, // new optional prop: array of filter values (e.g. ["BRAND", "PRODUCT"])
                             ...props
                           }) => {
  const options = React.Children.toArray(children).map((child) => {
    if (child?.type !== "option")
      throw new Error("TaleSelect cannot have children other than option");
    return {
      label: child.props.children,
      value: child.props.value,
      isDisabled: child.props.disabled,
      _meta: child.props["data-meta"] ? JSON.parse(child.props["data-meta"]) : null,
    };
  });

  const [internalValue, setinternalValue] = useState(multiple ? [] : (options.at(0) ?? null));
  const [activeFilter, setActiveFilter] = useState("ALL");

  const normalizeValue = useCallback(() => {
    if (multiple) {
      if (!Array.isArray(value)) return [];
      return options.filter((opt) => value.includes(opt.value));
    }
    return options.find((opt) => opt.value === value) || null;
  }, [multiple, options, value]);

  const handleChange = (selected) => {
    const value = multiple
        ? selected?.map((opt) => opt.value) ?? []
        : selected.value ?? "";
    if (!onChange) {
      setinternalValue(
          multiple ? value : options.find((e) => e.value === value)
      );
    } else onChange({ target: { ...props, value } });
  };

  const filteredOptions = useMemo(() => {
    if (!filterKey || !filterValues || activeFilter === "ALL") return options;
    return options.filter((opt) => opt._meta?.[filterKey] === activeFilter);
  }, [options, filterKey, filterValues, activeFilter]);

  const MenuList = useCallback(
      ({ children, ...menuProps }) => {
        if (!filterKey || !filterValues) {
          return <components.MenuList {...menuProps}>{children}</components.MenuList>;
        }
        return (
            <components.MenuList {...menuProps}>
              <div className="flex gap-1 px-2 py-2 border-b border-gray-100 sticky top-0 bg-white z-10">
                {["ALL", ...filterValues].map((f) => (
                    <button
                        key={f}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveFilter(f);
                        }}
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold border transition-colors ${
                            activeFilter === f
                                ? "bg-[#00BDA9] text-white border-[#00BDA9]"
                                : "bg-white text-gray-500 border-gray-200 hover:border-[#00BDA9] hover:text-[#00BDA9]"
                        }`}
                    >
                      {f === "ALL" ? "All" : f.charAt(0) + f.slice(1).toLowerCase()}
                    </button>
                ))}
              </div>
              {children}
            </components.MenuList>
        );
      },
      [filterKey, filterValues, activeFilter]
  );

  return (
      <div className={`flex flex-col gap-1 ${className ?? ""}`}>
        {label && (
            <span className={`font-semibold text-sm ${labelClassName ?? ""}`}>
          {label}
        </span>
        )}
        <TaleBorderFul role={role} width={width} height={height}>
          <div className="w-full z-30">
            <Select
                menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                menuPosition="fixed"
                classNamePrefix="tale-select"
                className={selectClassName}
                value={!onChange ? internalValue : normalizeValue()}
                onChange={handleChange}
                isMulti={multiple}
                options={filteredOptions}
                components={{ MenuList }}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: height,
                    border: "none",
                    boxShadow: "none",
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
                {...props}
            />
          </div>
        </TaleBorderFul>
      </div>
  );
};